import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Check, Calendar } from 'lucide-react'
import { motion } from 'framer-motion'
import { PageSeo, SectionHeading, Button, ServiceCard } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'
import { apiUrl } from '@/lib/api'
import type { RouteKey } from '@/i18n/routes'

interface NewsItem {
  id: string | number
  title: string
  excerpt?: string
  content?: string
  coverImage?: string
  publishedAt: string
  category?: string
  slug: string
}

const serviceKeys: { key: RouteKey; image: string; imageAlt: string }[] = [
  {
    key: 'breastSurgery',
    image: '/media/service-breast-surgery.webp',
    imageAlt: 'Chirurgia estetica del seno a Verona',
  },
  {
    key: 'bodySurgery',
    image: '/media/service-body-surgery.webp',
    imageAlt: 'Chirurgia estetica del corpo a Verona',
  },
  {
    key: 'faceSurgery',
    image: '/media/service-face-surgery.webp',
    imageAlt: 'Chirurgia estetica del viso a Verona',
  },
  {
    key: 'regenerative',
    image: '/media/service-regenerative.webp',
    imageAlt: 'Medicina rigenerativa a Verona',
  },
  {
    key: 'faceAesthetic',
    image: '/media/service-face-aesthetic.webp',
    imageAlt: 'Medicina estetica del viso a Verona',
  },
  {
    key: 'bodyAesthetic',
    image: '/media/service-body-aesthetic.webp',
    imageAlt: 'Medicina estetica del corpo a Verona',
  },
]

const teamMembers = ['dallantonio', 'mattiucci', 'rizzato'] as const

export function HomePage() {
  const { t } = useTranslation(['home', 'common'])
  const [news, setNews] = useState<NewsItem[]>([])
  const [newsLoading, setNewsLoading] = useState(true)

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch(apiUrl('/api/news?limit=2'))
        if (!res.ok) throw new Error('Failed to fetch news')
        const data = await res.json()
        setNews(data.news || [])
      } catch {
        setNews([])
      } finally {
        setNewsLoading(false)
      }
    }
    fetchNews()
  }, [])

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalBusiness',
      name: 'AD Aesthetics',
      description: t('home:seo.description'),
      url: typeof window !== 'undefined' ? window.location.origin : '',
      telephone: '+390458348902',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Gazzera, 1',
        addressLocality: 'Verona',
        postalCode: '37128',
        addressCountry: 'IT',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Physician',
      name: 'Dr. Alessandro Dall\'Antonia',
      medicalSpecialty: 'Plastic and Aesthetic Surgery',
      worksFor: { '@type': 'MedicalBusiness', name: 'AD Aesthetics' },
    },
  ]

  return (
    <>
      <PageSeo
        title={t('home:seo.title')}
        description={t('home:seo.description')}
        jsonLd={jsonLd}
      />

      <main>
        {/* Hero — solo immagine */}
        <section className="relative min-h-screen">
          <picture className="absolute inset-0 z-0">
            <source
              srcSet="/media/hero-home-mobile.png"
              media="(max-width: 768px)"
            />
            <img
              src="/media/hero-home-desktop.png"
              alt="AD Aesthetics — studio medico a Verona"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </picture>
        </section>

        {/* Services */}
        <section className="bg-ink-deep py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              title={t('home:services.title')}
              subtitle={t('home:services.subtitle')}
              centered
              light
              accent
              className="mb-12 md:mb-16"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {serviceKeys.map(({ key, image, imageAlt }, index) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="h-full"
                >
                  <ServiceCard
                    to={key}
                    title={t(`common:services.${key}`)}
                    description={t(`common:services.${key}Description`, '')}
                    imageSrc={image}
                    imageAlt={imageAlt}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Laser */}
        <section className="bg-cream py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="font-display text-h2 font-semibold text-ink">
                  {t('home:services.laserTitle')}
                </h2>
                <p className="mt-4 text-lead text-muted">
                  {t('home:services.laserDescription')}
                </p>
                <div className="mt-8">
                  <LocalizedLink to="laser">
                    <Button variant="primary-light">
                      {t('home:services.laserCta')}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </LocalizedLink>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="/media/home-laser.webp"
                  alt={t('home:services.laserTitle')}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Clinic */}
        <section className="bg-ink py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="order-2 lg:order-1 aspect-[4/3] rounded-xl overflow-hidden border border-white/10 bg-ink-deep">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/V1J7TYYAhfw?rel=0&modestbranding=1"
                  title={t('home:clinic.title')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="order-1 lg:order-2">
                <SectionHeading
                  title={t('home:clinic.title')}
                  light
                  accent
                />
                <p className="mt-6 text-white/80 leading-relaxed">
                  {t('home:clinic.description')}
                </p>
                <ul className="mt-6 space-y-3">
                  {[0, 1, 2].map((i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{t(`home:clinic.features.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="bg-ink-deep py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              title={t('home:team.title')}
              centered
              light
              accent
              className="mb-10 md:mb-14"
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
                className="rounded-xl overflow-hidden"
              >
                <img
                  src="/media/team-home.jpeg"
                  alt="Dr. Alessandro Dall'Antonia — AD Aesthetics"
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </motion.div>

              <div className="space-y-8">
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={member}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="border-b border-white/10 pb-8 last:border-b-0 last:pb-0"
                  >
                    <h3 className="font-display text-xl font-semibold text-white">
                      {t(`home:members.${member}.name`)}
                    </h3>
                    <p className="text-sm font-medium text-primary">
                      {t(`home:members.${member}.role`)}
                    </p>
                    <p className="mt-3 text-white/80 leading-relaxed">
                      {t(`home:members.${member}.bio`)}
                    </p>
                  </motion.div>
                ))}

                <blockquote className="pt-8 border-t border-white/10">
                  <p className="font-display text-xl italic text-primary leading-relaxed">
                    “{t('home:team.quote')}”
                  </p>
                  <cite className="mt-2 block text-sm text-white/60 not-italic">
                    {t('home:team.quoteAuthor')}
                  </cite>
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        {/* News */}
        <section className="bg-cream py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              title={t('home:news.title')}
              subtitle={t('home:news.subtitle')}
              centered
              className="mb-12 md:mb-16"
            />

            {newsLoading ? (
              <p className="text-center text-muted">{t('common:aria.loading')}...</p>
            ) : news.length === 0 ? (
              <div className="text-center">
                <p className="text-muted">{t('home:news.empty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {news.map((item) => (
                  <article
                    key={item.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm border border-silver hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-2/5 aspect-[16/10] md:aspect-auto bg-primary-soft flex items-center justify-center">
                        {item.coverImage ? (
                          <img
                            src={item.coverImage}
                            alt={item.title}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-display text-4xl text-primary/30">AD</span>
                        )}
                      </div>
                      <div className="md:w-3/5 p-6">
                        <div className="flex items-center gap-2 text-xs text-muted mb-2">
                          {item.category && (
                            <span className="px-2 py-1 bg-primary-soft text-ink rounded-md">
                              {item.category}
                            </span>
                          )}
                          <time dateTime={item.publishedAt}>
                            {new Date(item.publishedAt).toLocaleDateString()}
                          </time>
                        </div>
                        <h3 className="font-display text-lg font-semibold text-ink">
                          {item.title}
                        </h3>
                        {item.excerpt && (
                          <p className="mt-2 text-sm text-muted line-clamp-2">{item.excerpt}</p>
                        )}
                        <LocalizedLink
                          to={`blog?article=${item.slug}`}
                          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink hover:text-primary transition-colors"
                        >
                          {t('common:buttons.readMore')}
                          <ArrowRight className="w-4 h-4" />
                        </LocalizedLink>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-10 text-center">
              <LocalizedLink to="blog">
                <Button variant="outline-dark">
                  {t('home:news.cta')}
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-h2 font-semibold text-ink">
              {t('home:cta.title')}
            </h2>
            <p className="mt-4 text-lead text-ink/80 max-w-2xl mx-auto">
              {t('home:cta.subtitle')}
            </p>
            <div className="mt-8">
              <LocalizedLink to="bookConsultation">
                <Button variant="primary-light">
                  <Calendar className="w-4 h-4" />
                  {t('home:cta.button')}
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
